import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { handleLinkClick } from "@/Config/helper";
import { setStayGlobal, setSelectedCountryByUser } from "@/utils/cookieutils";
import { KNOWN_COUNTRIES } from "@/Config/countryConstants";
import styles from "./NavBar.module.css";

interface FlexibleMegamenuProps {
  onClose: () => void;
  megamenuData?: any;
}

const FlexibleMegamenu: React.FC<FlexibleMegamenuProps> = ({
  onClose,
  megamenuData,
}) => {
  const router = useRouter();

  const handleItemClick = (url: string) => {
    onClose();
    
    // Extract country from URL (e.g., '/nigeria' -> 'nigeria')
    const countryFromUrl = (url || "").replace(/^\//, '').split('/')[0];
    
    // Check if this is South Africa (Global) selection
    const isSouthAfrica = url === '/' || url === '' || url === '#' || 
                         countryFromUrl === 'south-africa' || 
                         countryFromUrl === 'south africa' ||
                         countryFromUrl === 'global';
    
    if (isSouthAfrica) {
      setStayGlobal();
    } else if (KNOWN_COUNTRIES.includes(countryFromUrl)) {
      setSelectedCountryByUser(countryFromUrl);
    }
    
    router.push(url || '#');
  };

  const hasSectionContent = (section: any) => {
    return (
      (section?.title && section.title.trim() !== "") ||
      (section?.items && section.items.length > 0)
    );
  };

  const hasCategoryContent = (category: any) => {
    return category?.title && category.title.trim() !== "";
  };

  if (!megamenuData) {
    return (
      <div className={styles.flexibleMegamenu}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <span>Fetching Mega Menu Data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.flexibleMegamenu}>
      <div className={styles.megamenuGrid}>
        <div className={styles.megamenuColumn}>
          {megamenuData?.categories
            ?.slice(0, 3)
            .map((category: any, index: number) => {
              if (!hasCategoryContent(category)) return null;

              return (
                <div key={category?.title || `category-${index}`} className={styles.megamenuCategory}>
                  <Link
                    href={category?.url || "#"}
                    className={styles.categoryTitle}
                    onClick={(e) =>
                      handleLinkClick(e, category?.url || "#", router)
                    }
                  >
                    {category?.title && <h3>{category.title}</h3>}
                  </Link>
                  {category?.sections?.map((section: any, sectionIndex: number) => {
                    if (!hasSectionContent(section)) return null;

                    return (
                      <div
                        key={section?.title || `section-${sectionIndex}`}
                        className={styles.megamenuSection}
                      >
                        {section?.title && section.title.trim() !== "" && (
                          <Link
                            href={section?.url || "#"}
                            className={styles.sectionTitle}
                            onClick={(e) =>
                              handleLinkClick(e, section?.url || "#", router)
                            }
                          >
                            {section?.title && <h4>{section.title}</h4>}
                          </Link>
                        )}
                        {section?.items?.map((item: any, itemIndex: number) => {
                          return (
                            <Link
                              key={item?.label || `item-${itemIndex}`}
                              href={item?.url || "#"}
                              className={styles.submenuItem}
                              onClick={() => handleItemClick(item?.url || "#")}
                            >
                              {item?.label}
                            </Link>
                          );
                        })}
                      </div>
                    );
                  })}
                  {index < 2 &&
                    hasCategoryContent(
                      megamenuData?.categories?.[index + 1]
                    ) && <div className={styles.categoryDivider} />}
                </div>
              );
            })}
        </div>

        <div className={styles.megamenuColumn}>
          {megamenuData?.categories
            ?.slice(3, 5)
            .map((category: any, index: number) => {
              if (!hasCategoryContent(category)) return null;

              return (
                <div key={category?.title || `category-${index}`} className={styles.megamenuCategory}>
                  <Link
                    href={category?.url || "#"}
                    className={styles.categoryTitle}
                    onClick={(e) =>
                      handleLinkClick(e, category?.url || "#", router)
                    }
                  >
                    <h3>{category?.title}</h3>
                  </Link>
                  {category?.sections?.map((section: any, sectionIndex: number) => {
                    if (!hasSectionContent(section)) return null;

                    return (
                      <div
                        key={section?.title || `section-${sectionIndex}`}
                        className={styles.megamenuSection}
                      >
                        {section?.title && section.title.trim() !== "" && (
                          <Link
                            href={section?.url || "#"}
                            className={styles.sectionTitle}
                            onClick={(e) =>
                              handleLinkClick(e, section?.url || "#", router)
                            }
                          >
                            {section?.title && <h4>{section.title}</h4>}
                          </Link>
                        )}
                        {section?.items?.map((item: any, itemIndex: number) => {
                          return (
                            <Link
                              key={item?.label || `item-${itemIndex}`}
                              href={item?.url || "#"}
                              className={styles.submenuItem}
                              onClick={() => handleItemClick(item?.url || "#")}
                            >
                              {item?.label}
                            </Link>
                          );
                        })}
                      </div>
                    );
                  })}
                  {index < 1 &&
                    hasCategoryContent(megamenuData?.categories?.[4]) && (
                      <div className={styles.categoryDivider} />
                    )}
                </div>
              );
            })}
        </div>

        <div className={styles.megamenuColumn}>
          {megamenuData?.categories
            ?.slice(5, 10)
            .map((category: any, index: number) => {
              if (!hasCategoryContent(category)) return null;

              return (
                <div key={category?.title || `category-${index}`} className={styles.megamenuCategory}>
                  <Link
                    href={category?.url || "#"}
                    className={styles.categoryTitle}
                    onClick={(e) =>
                      handleLinkClick(e, category?.url || "#", router)
                    }
                  >
                    <h3>{category?.title}</h3>
                  </Link>
                  {category?.sections?.map((section: any, sectionIndex: number) => {
                    if (!hasSectionContent(section)) return null;

                    return (
                      <div
                        key={section?.title || `section-${sectionIndex}`}
                        className={styles.megamenuSection}
                      >
                        {section?.title && section.title.trim() !== "" && (
                          <Link
                            href={section?.url || "#"}
                            className={styles.sectionTitle}
                            onClick={(e) =>
                              handleLinkClick(e, section?.url || "#", router)
                            }
                          >
                            {section?.title && <h4>{section.title}</h4>}
                          </Link>
                        )}
                        {section?.items?.map((item: any, itemIndex: number) => {
                          return (
                            <Link
                              key={item?.label || `item-${itemIndex}`}
                              href={item?.url || "#"}
                              className={styles.submenuItem}
                              onClick={() => handleItemClick(item?.url || "#")}
                            >
                              {item?.label}
                            </Link>
                          );
                        })}
                      </div>
                    );
                  })}
                  {index < 1 &&
                    hasCategoryContent(megamenuData?.categories?.[6]) && (
                      <div className={styles.categoryDivider} />
                    )}
                </div>
              );
            })}
        </div>

        <div className={styles.megamenuColumn}>
          {megamenuData?.certificationCourses?.items?.length > 0 && (
            <div className={styles.megamenuCategory}>
              <Link
                href={megamenuData?.certificationCourses?.url || "#"}
                className={styles.categoryTitle}
                onClick={(e) =>
                  handleLinkClick(
                    e,
                    megamenuData?.certificationCourses?.url || "#",
                    router
                  )
                }
              >
                <h3>{megamenuData?.certificationCourses?.title}</h3>
              </Link>
              <div className={styles.certificationGrid}>
                {megamenuData?.certificationCourses?.items?.map(
                  (course: any, courseIndex: number) => (
                    <Link
                      key={course?.label || `course-${courseIndex}`}
                      href={course?.url || "#"}
                      className={styles.submenuItem}
                      onClick={() => handleItemClick(course?.url || "#")}
                    >
                      {course?.label}
                    </Link>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlexibleMegamenu;
